/**
 * CSS conic-gradient() polyfill
 * By Lea Verou — http://lea.verou.me
 * MIT license
 */

const π = Math.PI;
const τ = 2 * π;
const ε = 0.00001;
const deg = π / 180;

export default function ConicGradient(o) {
  if (!document) {
    return;
  }

  ConicGradient.all.push(this);

  o = o || {};

  this.canvas = document.createElement("canvas");
  this.context = this.canvas.getContext("2d");

  this.repeating = Boolean(o.repeating);

  this.size = o.size || 1000;

  this.canvas.width = this.canvas.height = this.size;

  this.stops = (o.stops || "").split(/\s*,(?![^(]*\))\s*/); // commas that are not followed by a ) without a ( first

  this.from = 0;

  for (let i = 0; i < this.stops.length; i++) {
    if (this.stops[i]) {
      const stop = (this.stops[i] = new ConicGradient.ColorStop(this, this.stops[i]));

      if (stop.next) {
        this.stops.splice(i + 1, 0, stop.next);
        i++;
      }
    } else {
      this.stops.splice(i, 1);
      i--;
    }
  }

  if (this.stops[0].color.indexOf("from") === 0) {
    this.from = this.stops[0].pos * 360;
    this.stops.shift();
  }
  // Normalize stops

  // Add dummy first stop or set first stop’s position to 0 if it doesn’t have one
  if (this.stops[0].pos === undefined) {
    this.stops[0].pos = 0;
  } else if (this.stops[0].pos > 0) {
    const first = this.stops[0].clone();
    first.pos = 0;
    this.stops.unshift(first);
  }

  // Add dummy last stop or set first stop’s position to 100% if it doesn’t have one
  if (this.stops[this.stops.length - 1].pos === undefined) {
    this.stops[this.stops.length - 1].pos = 1;
  } else if (!this.repeating && this.stops[this.stops.length - 1].pos < 1) {
    const last = this.stops[this.stops.length - 1].clone();
    last.pos = 1;
    this.stops.push(last);
  }

  this.stops.forEach(function (stop, i) {
    if (stop.pos === undefined) {
      // Evenly space color stops with no position
      for (let j = i + 1; this[j]; j++) {
        if (this[j].pos !== undefined) {
          stop.pos = this[i - 1].pos + (this[j].pos - this[i - 1].pos) / (j - i + 1);
          break;
        }
      }
    } else if (i > 0) {
      // Normalize color stops whose position is smaller than the position of the stop before them
      stop.pos = Math.max(stop.pos, this[i - 1].pos);
    }
  }, this.stops);

  if (this.repeating) {
    // Repeat color stops until >= 1
    const stops = this.stops.slice();
    const lastStop = stops[stops.length - 1];
    const difference = lastStop.pos - stops[0].pos;

    for (let i = 0; this.stops[this.stops.length - 1].pos < 1 && i < 10000; i++) {
      for (let j = 0; j < stops.length; j++) {
        const s = stops[j].clone();
        s.pos = s.pos + (i + 1) * difference;

        this.stops.push(s);
      }
    }
  }

  this.paint();
}

ConicGradient.all = [];

ConicGradient.prototype = {
  toString: function () {
    return `url('${this.dataURL}')`;
  },

  get dataURL() {
    // IE/Edge only render data-URI based background-image when the image data
    // is escaped.
    // Ref: https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/7157459/#comment-3
    return `data:image/svg+xml,${encodeURIComponent(this.svg)}`;
  },

  get blobURL() {
    // Warning: Flicker when updating due to slow blob: URL resolution :(
    // TODO cache this and only update it when color stops change
    return URL.createObjectURL(new Blob([this.svg], { type: "image/svg+xml" }));
  },

  get svg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" preserveAspectRatio="none">
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">' +
      '<image width="100" height="100%" xlink:href="${this.png}" /></svg></svg>`;
  },

  get png() {
    return this.canvas.toDataURL();
  },

  get r() {
    return (Math.sqrt(2) * this.size) / 2;
  },

  // Paint the conical gradient on the canvas
  // Algorithm inspired from http://jsdo.it/akm2/yr9B
  paint: function () {
    const c = this.context;

    const radius = this.r;
    const x = this.size / 2;

    let stopIndex = 0; // The index of the current color
    let stop = this.stops[stopIndex],
      prevStop;

    let diff, t;

    // Transform coordinate system so that angles start from the top left, like in CSS
    c.translate(this.size / 2, this.size / 2);
    c.rotate(-90 * deg);
    c.rotate(this.from * deg);
    c.translate(-this.size / 2, -this.size / 2);

    const minusColor = (c, i) => stop.color[i] - c;
    const diffFunc = (d, i) => {
      const ret = d * t + prevStop.color[i];
      return i < 3 ? ret & 255 : ret;
    };

    for (let i = 0; i < 360; ) {
      let sameColor;
      if (i / 360 + ε >= stop.pos) {
        // Switch color stop
        do {
          prevStop = stop;

          stopIndex++;
          stop = this.stops[stopIndex];
        } while (stop && stop !== prevStop && stop.pos === prevStop.pos);

        if (!stop) {
          break;
        }

        sameColor = `${prevStop.color}` === `${stop.color}` && prevStop !== stop;

        diff = prevStop.color.map(minusColor);
      }

      t = (i / 360 - prevStop.pos) / (stop.pos - prevStop.pos);

      const interpolated = sameColor ? stop.color : diff.map(diffFunc);

      // Draw a series of arcs, 1deg each
      c.fillStyle = `rgba(${interpolated.join(",")})`;
      c.beginPath();
      c.moveTo(x, x);

      let θ = 0.5;
      if (sameColor) {
        θ = 360 * (stop.pos - prevStop.pos);
      }

      let beginArg = i * deg;
      beginArg = Math.min(360 * deg, beginArg);

      // .02: To prevent empty blank line and corresponding moire
      // only non-alpha colors are cared now
      let endArg = beginArg + θ * deg;
      endArg = Math.min(360 * deg, endArg + 0.02);

      c.arc(x, x, radius, beginArg, endArg);

      c.closePath();
      c.fill();

      i = i + θ;
    }
  },
};

ConicGradient.ColorStop = function (gradient, stop) {
  this.gradient = gradient;

  if (stop) {
    const parts = stop.match(
      /^(.+?)(?:\s+([\d.]+)(%|deg|turn|grad|rad)?)?(?:\s+([\d.]+)(%|deg|turn|grad|rad)?)?\s*$/,
    );

    this.color = ConicGradient.ColorStop.colorToRGBA(parts[1]);

    if (parts[2]) {
      const [, , pos, unit] = parts;

      if (unit === "%" || (pos === "0" && !unit)) {
        this.pos = pos / 100;
      } else if (unit === "turn") {
        this.pos = Number(pos);
      } else if (unit === "deg") {
        this.pos = pos / 360;
      } else if (unit === "grad") {
        this.pos = pos / 400;
      } else if (unit === "rad") {
        this.pos = pos / τ;
      }
    }

    if (parts[4]) {
      this.next = new ConicGradient.ColorStop(gradient, `${parts[1]} ${parts[4]}${parts[5]}`);
    }
  }
};

ConicGradient.ColorStop.prototype = {
  clone: function () {
    const ret = new ConicGradient.ColorStop(this.gradient);
    ret.color = this.color;
    ret.pos = this.pos;

    return ret;
  },

  toString: function () {
    return `rgba(${this.color.join(", ")}) ${this.pos * 100}%`;
  },
};

ConicGradient.ColorStop.colorToRGBA = (color) => {
  if (!Array.isArray(color) && color.indexOf("from") === -1) {
    let c, rgba;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(color)) {
      c = color.substring(1).split("");
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = `0x${c.join("")}`;
      rgba = [(c >> 16) & 255, (c >> 8) & 255, c & 255, 1];
    }

    return rgba || [0, 0, 0, 0];
  }
};
